import React, { useState } from "react";
import {
	CheckCircle2,
	CircleDot,
	Copy,
	Download,
	ExternalLink,
	FileText,
	History,
	Info,
	Loader2,
	Search,
	X,
	XCircle,
} from "lucide-react";
import { ProviderIcon } from "../shared/ProviderIcon";
import { toAnalyseProvider } from "./analysisStrategy/providerMap";
import { normalizePersonaRecord, personaContextDetailRows } from "./writingPersonaDisplay";

const CONTEXT_TABS = [
	{ id: "brief", label: "Brief & Gaps" },
	{ id: "brand", label: "Brand & Voice" },
	{ id: "signals", label: "Signals" },
];

function InfoHint({ text }) {
	return (
		<span className="monitoring__writing-context-hint" title={text}>
			<Info size={12} aria-hidden />
		</span>
	);
}

function ContextSection({ title, hint, children, className = "" }) {
	return (
		<section className={`monitoring__writing-context-section${className ? ` ${className}` : ""}`}>
			<h4 className="monitoring__writing-context-section-title">
				{title}
				{hint ? <InfoHint text={hint} /> : null}
			</h4>
			{children}
		</section>
	);
}

function ContextBadge({ tone = "neutral", children }) {
	return (
		<span
			className={`monitoring__writing-context-badge monitoring__writing-context-badge--${tone}`}
		>
			{children}
		</span>
	);
}

function importanceTone(importance) {
	if (importance === "critical") return "critical";
	if (importance === "high") return "high";
	if (importance === "medium") return "medium";
	if (importance === "low") return "low";
	return "neutral";
}

function LabeledValue({ label, value }) {
	if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
		return null;
	}
	const display = Array.isArray(value)
		? value.join(", ")
		: typeof value === "object"
			? JSON.stringify(value)
			: String(value);
	return (
		<div className="monitoring__writing-context-labeled">
			<span className="fw-semibold">{label}:</span>{" "}
			<span className="monitoring__writing-context-muted">{display}</span>
		</div>
	);
}

function RemainingFields({ obj, known, omit = [] }) {
	if (!obj || typeof obj !== "object") return null;
	const rest = Object.entries(obj).filter(
		([key]) => !known.includes(key) && !omit.includes(key)
	);
	if (!rest.length) return null;
	return (
		<div className="d-flex flex-column gap-1">
			{rest.map(([key, value]) => (
				<LabeledValue key={key} label={key} value={value} />
			))}
		</div>
	);
}

function ContentGapCard({ item }) {
	const heading = item.gap ?? item.topic;
	const body = item.instruction ?? item.evidence_from_competitor;
	const providerCoverage = item.provider_coverage;
	const signalId = item.signal_id;
	const evidenceDomain = item.evidence_domain;
	const suggestedSection = item.suggested_section;
	const importance = item.importance;
	const hasBadges = importance || providerCoverage != null || signalId;

	return (
		<li className="monitoring__writing-context-card">
			{hasBadges ? (
				<div className="monitoring__writing-context-card-badges">
					{importance ? (
						<ContextBadge tone={importanceTone(importance)}>
							{String(importance).toUpperCase()}
						</ContextBadge>
					) : null}
					{providerCoverage != null ? (
						<ContextBadge tone="neutral">{providerCoverage}/3 providers</ContextBadge>
					) : null}
					{signalId ? <ContextBadge tone="mono">{signalId}</ContextBadge> : null}
				</div>
			) : null}
			{heading ? <p className="monitoring__writing-context-card-title">{heading}</p> : null}
			{body ? <p className="monitoring__writing-context-card-body">{body}</p> : null}
			{(evidenceDomain || suggestedSection) && (
				<div className="monitoring__writing-context-meta">
					{evidenceDomain ? <span>seen on {evidenceDomain}</span> : null}
					{suggestedSection ? <span>section: {suggestedSection}</span> : null}
				</div>
			)}
			<RemainingFields
				obj={item}
				known={[
					"gap",
					"topic",
					"instruction",
					"evidence_from_competitor",
					"importance",
					"provider_coverage",
					"signal_id",
					"evidence_domain",
					"suggested_section",
				]}
			/>
		</li>
	);
}

function getPageHref(page) {
	if (page?.url) return String(page.url);
	if (page?.domain) {
		const domain = String(page.domain).replace(/^https?:\/\//, "");
		return `https://${domain}`;
	}
	return null;
}

function getDomainLabel(page) {
	if (page?.domain) return String(page.domain).replace(/^www\./, "");
	if (page?.url) {
		try {
			return new URL(String(page.url)).hostname.replace(/^www\./, "");
		} catch {
			return String(page.url);
		}
	}
	return "Unknown";
}

function formatTruncatedUrl(url, maxLength = 52) {
	if (!url) return "";
	const raw = String(url);
	try {
		const parsed = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
		const display = `${parsed.hostname.replace(/^www\./, "")}${parsed.pathname}${parsed.search}`;
		const trimmed =
			display.endsWith("/") && display.length > 1 ? display.slice(0, -1) : display;
		if (trimmed.length <= maxLength) return trimmed;
		return `${trimmed.slice(0, maxLength - 3)}...`;
	} catch {
		if (raw.length <= maxLength) return raw;
		return `${raw.slice(0, maxLength - 3)}...`;
	}
}

function getReferenceTag(page, fallback = "Competitor") {
	const tag = page?.tag ?? page?.role ?? page?.type ?? page?.label;
	return tag ? String(tag) : fallback;
}

function ReferencePageCard({ page, tag = "Competitor" }) {
	const href = getPageHref(page);
	const domain = getDomainLabel(page);
	const title = page?.title ? String(page.title) : null;
	const displayTag = getReferenceTag(page, tag);

	if (!href) {
		return (
			<div className="monitoring__writing-context-ref-card monitoring__writing-context-ref-card--static">
				<div className="monitoring__writing-context-ref-card-main">
					<div className="monitoring__writing-context-ref-card-head">
						<span className="monitoring__writing-context-ref-domain">{domain}</span>
						<span className="monitoring__writing-context-ref-tag">{displayTag}</span>
					</div>
					{title ? (
						<p className="monitoring__writing-context-ref-title">{title}</p>
					) : null}
				</div>
			</div>
		);
	}

	return (
		<a
			href={href}
			target="_blank"
			rel="noreferrer"
			className="monitoring__writing-context-ref-card"
		>
			<div className="monitoring__writing-context-ref-card-main">
				<div className="monitoring__writing-context-ref-card-head">
					<span className="monitoring__writing-context-ref-domain">{domain}</span>
					<span className="monitoring__writing-context-ref-tag">{displayTag}</span>
				</div>
				{title ? <p className="monitoring__writing-context-ref-title">{title}</p> : null}
			</div>
			<ExternalLink size={14} className="monitoring__writing-context-ref-icon" aria-hidden />
		</a>
	);
}

function OwnBrandContextCard({ page }) {
	const href = getPageHref(page);
	const title =
		page?.title != null && String(page.title).trim() !== ""
			? String(page.title)
			: "Own-site page";
	const urlLabel = href ? formatTruncatedUrl(href) : getDomainLabel(page);

	const urlNode = href ? (
		<a href={href} target="_blank" rel="noreferrer" className="monitoring__writing-context-brand-url">
			<span className="monitoring__writing-context-brand-url-text">{urlLabel}</span>
			<ExternalLink size={12} className="flex-shrink-0" aria-hidden />
		</a>
	) : (
		<span className="monitoring__writing-context-brand-url monitoring__writing-context-brand-url--static">
			<span className="monitoring__writing-context-brand-url-text">{urlLabel}</span>
		</span>
	);

	return (
		<div className="monitoring__writing-context-brand-card">
			<div className="monitoring__writing-context-brand-badge">
				<CircleDot size={10} aria-hidden />
				Active Page Context
			</div>
			<h4 className="monitoring__writing-context-brand-heading">{title}</h4>
			{urlNode}
			<p className="monitoring__writing-context-brand-note">
				<FileText size={12} className="flex-shrink-0" aria-hidden />
				Full page content indexed and sent to LLM prompt context.
			</p>
		</div>
	);
}

function RecommendationBriefCard({ recommendation }) {
	const summary = recommendation.summary;
	const reason = recommendation.reason;
	const target = recommendation.target;

	return (
		<div className="monitoring__writing-context-brief-card">
			{summary ? <p className="monitoring__writing-context-brief-summary">{summary}</p> : null}
			{reason?.summary ? (
				<div>
					<p className="monitoring__writing-context-brief-why-label">Why:</p>
					<p className="monitoring__writing-context-brief-why">{reason.summary}</p>
				</div>
			) : null}
			{reason?.evidence?.length ? (
				<ul className="list-unstyled d-flex flex-column gap-2 mb-0">
					{reason.evidence.map((entry, index) => (
						<li key={index} className="monitoring__writing-context-inset-card">
							{entry.text}
							{entry.source_url ? (
								<>
									{" "}
									<a href={entry.source_url} target="_blank" rel="noreferrer">
										source
									</a>
								</>
							) : null}
						</li>
					))}
				</ul>
			) : null}
			{target ? (
				<div className="monitoring__writing-context-meta">
					{target.content_type != null ? (
						<span>type: {String(target.content_type)}</span>
					) : null}
					{target.suggested_title != null ? (
						<span>title idea: {String(target.suggested_title)}</span>
					) : null}
					{target.suggested_url != null ? (
						<a href={String(target.suggested_url)} target="_blank" rel="noreferrer">
							{String(target.suggested_url)}
						</a>
					) : null}
					{target.url != null ? (
						<a href={String(target.url)} target="_blank" rel="noreferrer">
							{String(target.url)}
						</a>
					) : null}
				</div>
			) : null}
			<RemainingFields
				obj={recommendation}
				known={["title", "summary", "reason", "target"]}
			/>
		</div>
	);
}

function SignalCard({ signal }) {
	const importance = signal.importance;

	return (
		<li className="monitoring__writing-context-signal-card">
			<div className="monitoring__writing-context-card-badges monitoring__writing-context-card-badges--split">
				<span className="monitoring__writing-context-signal-name">
					{String(signal.name ?? "")}
				</span>
				{typeof importance === "string" ? (
					<ContextBadge tone={importanceTone(importance)}>
						{importance.toUpperCase()}
					</ContextBadge>
				) : null}
			</div>
			{signal.description != null ? (
				<p className="monitoring__writing-context-signal-desc">{String(signal.description)}</p>
			) : null}
			<RemainingFields
				obj={signal}
				known={[
					"name",
					"description",
					"importance",
					"id",
					"category",
					"evidence",
					"supporting_competitors",
					"provider_coverage",
					"coverage_count",
				]}
			/>
		</li>
	);
}

function BriefAndGapsTab({ payload }) {
	const contentGaps = payload.content_gaps ?? payload.content_requirements ?? [];
	const pages = payload.reference_pages ?? [];
	const competitorDomains = payload.competitor_domains ?? [];
	const ownBrandContext = payload.own_brand_context ?? [];
	const background = payload.background;
	const opportunity = background?.opportunity;
	const promptCtx = background?.prompt;

	return (
		<>
			{payload.recommendation ? (
				<ContextSection
					title="Recommendation brief"
					hint="Frames the whole page: its summary/reason set the overall angle the model writes toward."
				>
					<RecommendationBriefCard recommendation={payload.recommendation} />
				</ContextSection>
			) : null}

			<ContextSection
				title={`Content gaps / requirements (${contentGaps.length})`}
				hint="Each edit must cite which gap motivated it; on a new page, these become outline sections."
			>
				{contentGaps.length > 0 ? (
					<ul className="list-unstyled d-flex flex-column gap-2 mb-0">
						{contentGaps.map((gap, index) => (
							<ContentGapCard key={index} item={gap} />
						))}
					</ul>
				) : (
					<p className="monitoring__writing-context-muted mb-0">None.</p>
				)}
			</ContextSection>

			{payload.facts_to_verify?.length ? (
				<ContextSection
					title={`Facts to verify (${payload.facts_to_verify.length})`}
					hint="Claim types the model must never invent without own-brand evidence."
				>
					<ul className="list-unstyled d-flex flex-column gap-2 mb-0">
						{payload.facts_to_verify.map((fact, index) => (
							<li key={index} className="monitoring__writing-context-card">
								{fact.required ? (
									<div className="monitoring__writing-context-card-badges">
										<ContextBadge tone="required">REQUIRED</ContextBadge>
									</div>
								) : null}
								<p className="monitoring__writing-context-card-body mb-0">
									{fact.description}
								</p>
							</li>
						))}
					</ul>
				</ContextSection>
			) : null}

			<ContextSection
				title={`Competitor / reference pages (${pages.length || competitorDomains.length})`}
				hint="Hard allowlist — the model may only name domains/pages from this list."
				className="monitoring__writing-context-section--ref-pages"
			>
				{pages.length > 0 ? (
					<div className="monitoring__writing-context-ref-list">
						{pages.map((page, index) => (
							<ReferencePageCard key={index} page={page} />
						))}
					</div>
				) : competitorDomains.length > 0 ? (
					<div className="monitoring__writing-context-ref-list">
						{competitorDomains.map((domain) => (
							<ReferencePageCard
								key={domain}
								page={{ domain, tag: "Competitor" }}
							/>
						))}
					</div>
				) : (
					<p className="monitoring__writing-context-muted mb-0">None.</p>
				)}
			</ContextSection>

			<ContextSection
				title={`Own brand context (${ownBrandContext.length})`}
				hint="The only source the model may pull a specific fact from."
				className="monitoring__writing-context-section--own-brand"
			>
				{ownBrandContext.length > 0 ? (
					<div className="monitoring__writing-context-brand-list">
						{ownBrandContext.map((page, index) => (
							<OwnBrandContextCard key={index} page={page} />
						))}
					</div>
				) : (
					<p className="monitoring__writing-context-muted mb-0">
						None — no supplementary own-site pages were found or fetched.
					</p>
				)}
			</ContextSection>

			{background && (opportunity?.reason || promptCtx) ? (
				<ContextSection title="Background" hint="Why this prompt matters.">
					{opportunity?.reason ? (
						<p className="monitoring__writing-context-card-body mb-2">
							{opportunity.score != null ? (
								<span className="fw-semibold">Opportunity {opportunity.score}/100 — </span>
							) : null}
							{opportunity.reason}
						</p>
					) : null}
					{promptCtx ? (
						<p className="monitoring__writing-context-meta mb-0">
							{[promptCtx.intent, promptCtx.topic, promptCtx.industry]
								.filter(Boolean)
								.join(" • ")}
						</p>
					) : null}
				</ContextSection>
			) : null}
		</>
	);
}

function PersonaContextCard({ rawPersona }) {
	const persona = normalizePersonaRecord(rawPersona);
	if (!persona) return null;

	const title =
		persona.name || (persona.id != null ? `Persona #${persona.id}` : "Persona");
	const detailRows = personaContextDetailRows(persona);
	const longFormKeys = new Set(["likes", "dislikes", "interests", "notes"]);
	const inlineRows = detailRows.filter((row) => !longFormKeys.has(row.key));
	const blockRows = detailRows.filter((row) => longFormKeys.has(row.key));

	return (
		<div className="monitoring__writing-context-card">
			<p className="monitoring__writing-context-card-title mb-1">{title}</p>
			{inlineRows.length ? (
				<div className="monitoring__writing-context-persona-details mb-2">
					{inlineRows.map((row) => (
						<LabeledValue key={row.key} label={row.label} value={row.value} />
					))}
				</div>
			) : null}
			{blockRows.map((row) => (
				<div key={row.key} className="monitoring__writing-context-persona-block mb-2">
					<p className="monitoring__writing-context-persona-block-label mb-1">
						{row.label}
					</p>
					<p className="monitoring__writing-context-card-body mb-0">{row.value}</p>
				</div>
			))}
			<RemainingFields
				obj={rawPersona}
				known={[
					"name",
					"id",
					"persona_id",
					"personaId",
					"gender",
					"age_range",
					"ageRange",
					"location",
					"tone",
					"notes",
					"profession",
					"language",
					"likes",
					"dislikes",
					"interests",
				]}
			/>
		</div>
	);
}

function BrandAndVoiceTab({ payload }) {
	const persona = payload.persona;
	const brandGuidelines = payload.brand_guidelines;
	const doList = brandGuidelines?.do_list ?? [];
	const dontList = brandGuidelines?.dont_list ?? [];
	const hasPersona =
		persona && typeof persona === "object" && normalizePersonaRecord(persona) != null;

	return (
		<>
			<ContextSection
				title="Persona"
				hint="Who this content should read well to — shapes tone and framing only."
			>
				{hasPersona ? (
					<PersonaContextCard rawPersona={persona} />
				) : (
					<p className="monitoring__writing-context-muted mb-0">No persona selected.</p>
				)}
			</ContextSection>

			<ContextSection
				title="Brand guidelines"
				hint="Sets the writing voice (do/don't rules, examples)."
			>
				{brandGuidelines &&
				(brandGuidelines.voice || doList.length || dontList.length || brandGuidelines.examples) ? (
					<div className="d-flex flex-column gap-2">
						{brandGuidelines.voice != null ? (
							<p className="monitoring__writing-context-card-body mb-0">
								{String(brandGuidelines.voice)}
							</p>
						) : null}
						{doList.length || dontList.length ? (
							<div className="d-flex flex-column gap-2">
								{doList.map((item, index) => (
									<div
										key={`do-${index}`}
										className="monitoring__writing-context-do-item"
									>
										<CheckCircle2 size={14} className="flex-shrink-0 mt-1" aria-hidden />
										<span>{item}</span>
									</div>
								))}
								{dontList.map((item, index) => (
									<div
										key={`dont-${index}`}
										className="monitoring__writing-context-dont-item"
									>
										<XCircle size={14} className="flex-shrink-0 mt-1" aria-hidden />
										<span>{item}</span>
									</div>
								))}
							</div>
						) : null}
						{brandGuidelines.examples != null ? (
							<p className="monitoring__writing-context-note mb-0">
								{String(brandGuidelines.examples)}
							</p>
						) : null}
						<RemainingFields
							obj={brandGuidelines}
							known={["voice", "do_list", "dont_list", "examples"]}
						/>
					</div>
				) : (
					<p className="monitoring__writing-context-muted mb-0">
						No brand guidelines configured for this brand.
					</p>
				)}
			</ContextSection>
		</>
	);
}

function SignalsTab({ payload, instructions }) {
	const searchFanout = payload.search_fanout ?? [];
	const providersAlreadyMentioned =
		payload.providers_where_brand_already_mentioned ?? [];
	const background = payload.background;
	const relevantSignals = background?.relevant_signals ?? [];

	return (
		<>
			<ContextSection
				title={`Search fanout (${searchFanout.length})`}
				hint="Real queries AI providers issued while researching this prompt."
			>
				{searchFanout.length > 0 ? (
					<ul className="list-unstyled d-flex flex-column gap-1 mb-0">
						{searchFanout.map((query, index) => (
							<li key={index} className="monitoring__writing-context-fanout-item">
								<Search size={13} className="flex-shrink-0" aria-hidden />
								<span className="text-break flex-grow-1">{query.query}</span>
								<span className="d-inline-flex gap-1 flex-shrink-0">
									{(query.providers ?? []).map((provider) => (
										<ProviderIcon
											key={provider}
											provider={toAnalyseProvider(provider)}
											size={12}
										/>
									))}
								</span>
							</li>
						))}
					</ul>
				) : (
					<p className="monitoring__writing-context-muted mb-0">None.</p>
				)}
				{providersAlreadyMentioned.length > 0 ? (
					<p className="monitoring__writing-context-note mb-0 mt-2">
						Brand already mentioned overall by:{" "}
						{providersAlreadyMentioned.map((provider) => (
							<ProviderIcon
								key={provider}
								provider={toAnalyseProvider(provider)}
								size={12}
								className="mx-1"
							/>
						))}
					</p>
				) : null}
			</ContextSection>

			<ContextSection
				title={`Relevant signals (${relevantSignals.length})`}
				hint="Evidence-backed signals that informed this run."
			>
				{relevantSignals.length > 0 ? (
					<ul className="list-unstyled d-flex flex-column gap-2 mb-0">
						{relevantSignals.map((signal, index) => (
							<SignalCard key={index} signal={signal} />
						))}
					</ul>
				) : (
					<p className="monitoring__writing-context-muted mb-0">None.</p>
				)}
			</ContextSection>

			{/* <ContextSection
				title="Final LLM template"
				hint="The exact system prompt and JSON sent to the model."
			>
				<details>
					<summary className="monitoring__writing-context-summary">
						Show the raw instructions + input JSON
					</summary>
					<div className="mt-2">
						<p className="monitoring__writing-context-muted mb-1">Instructions:</p>
						<pre className="monitoring__writing-context-pre">{instructions}</pre>
						<p className="monitoring__writing-context-muted mb-1 mt-3">Input JSON:</p>
						<pre className="monitoring__writing-context-pre">
							{JSON.stringify(payload, null, 2)}
						</pre>
					</div>
				</details>
			</ContextSection> */}
		</>
	);
}

export function WritingAssistantContextBody({ run, activeTab = "brief" }) {
	const payload = run?.result?.recommendation?.llm_input_payload;
	const instructions = run?.result?.llm_instructions;

	if (!payload) {
		return (
			<p className="monitoring__writing-context-muted mb-0">
				No context recorded for this draft — it was generated before this feature
				existed. Regenerate the draft to capture what&apos;s sent to the model.
			</p>
		);
	}

	return (
		<div className="monitoring__writing-context-body">
			{activeTab === "brief" ? <BriefAndGapsTab payload={payload} /> : null}
			{activeTab === "brand" ? <BrandAndVoiceTab payload={payload} /> : null}
			{activeTab === "signals" ? (
				<SignalsTab payload={payload} instructions={instructions} />
			) : null}
		</div>
	);
}

function handlePanelToggle(event, onToggle) {
	event.currentTarget.blur();
	onToggle();
}

export function WritingAssistantCopyToggle({
	onCopy,
	disabled = false,
	isCopying = false,
	placement = "toolbar",
}) {
	const label = isCopying ? "Copying document…" : "Copy full document";
	return (
		<button
			type="button"
			className={`btn btn-sm d-inline-flex align-items-center justify-content-center monitoring__writing-copy-trigger monitoring__writing-copy-trigger--${placement}`}
			onClick={(event) => {
				event.currentTarget.blur();
				onCopy?.();
			}}
			disabled={disabled || isCopying}
			aria-label={label}
			title={label}
		>
			{isCopying ? (
				<Loader2 size={16} className="monitoring__writing-spin" aria-hidden />
			) : (
				<Copy size={16} aria-hidden />
			)}
		</button>
	);
}

export function WritingAssistantDownloadToggle({
	onDownload,
	disabled = false,
	isDownloading = false,
	placement = "toolbar",
}) {
	const label = isDownloading ? "Preparing download…" : "Download draft as Word document";
	return (
		<button
			type="button"
			className={`btn btn-sm d-inline-flex align-items-center justify-content-center monitoring__writing-download-trigger monitoring__writing-download-trigger--${placement}`}
			onClick={(event) => {
				event.currentTarget.blur();
				onDownload?.();
			}}
			disabled={disabled || isDownloading}
			aria-label={label}
			title={label}
		>
			{isDownloading ? (
				<Loader2 size={16} className="monitoring__writing-spin" aria-hidden />
			) : (
				<Download size={16} aria-hidden />
			)}
		</button>
	);
}

export function WritingAssistantHistoryToggle({
	isOpen,
	onToggle,
	disabled = false,
	placement = "toolbar",
}) {
	const label = isOpen ? "Hide History" : "Show History";
	return (
		<button
			type="button"
			className={`btn btn-sm d-inline-flex align-items-center gap-2 monitoring__writing-history-trigger monitoring__writing-history-trigger--${placement}${
				isOpen ? " monitoring__writing-history-trigger--active" : ""
			}`}
			onClick={(event) => handlePanelToggle(event, onToggle)}
			disabled={disabled}
			aria-pressed={isOpen}
			aria-expanded={isOpen}
			aria-controls="writing-assistant-history-panel"
			aria-label={label}
			title={label}
		>
			<History size={16} aria-hidden />
			{placement === "toolbar" ? <span>History</span> : null}
		</button>
	);
}

export function WritingAssistantContextToggle({
	isOpen,
	onToggle,
	disabled = false,
	placement = "toolbar",
}) {
	const actionLabel = isOpen ? "Hide Context" : "Show Context";
	return (
		<button
			type="button"
			className={`btn btn-sm d-inline-flex align-items-center gap-2 monitoring__writing-context-trigger monitoring__writing-context-trigger--${placement}${
				isOpen ? " monitoring__writing-context-trigger--active" : ""
			}`}
			onClick={(event) => handlePanelToggle(event, onToggle)}
			disabled={disabled}
			aria-pressed={isOpen}
			aria-expanded={isOpen}
			aria-controls="writing-assistant-context-panel"
			aria-label={actionLabel}
			title={actionLabel}
		>
			<FileText size={16} aria-hidden />
			{placement === "toolbar" ? <span>Context</span> : null}
		</button>
	);
}

export function WritingAssistantContextPanel({ run, onClose }) {
	const [activeTab, setActiveTab] = useState("brief");

	return (
		<aside
			id="writing-assistant-context-panel"
			className="monitoring__writing-context-panel"
			aria-label="What was sent to the model"
		>
			<div className="monitoring__writing-context-panel-head">
				<div className="monitoring__writing-context-panel-head-row">
					<h3 className="monitoring__writing-context-panel-title">
						What was sent to the model
					</h3>
					{onClose ? (
						<button
							type="button"
							className="monitoring__writing-context-panel-close"
							onClick={(event) => handlePanelToggle(event, onClose)}
							aria-label="Close context panel"
							title="Close"
						>
							<X size={16} aria-hidden />
						</button>
					) : null}
				</div>
				<div
					className="monitoring__writing-context-tabs"
					role="tablist"
					aria-label="Context sections"
				>
					{CONTEXT_TABS.map((tab) => (
						<button
							key={tab.id}
							type="button"
							role="tab"
							id={`writing-context-tab-${tab.id}`}
							className={`monitoring__writing-context-tab${
								activeTab === tab.id ? " is-active" : ""
							}`}
							aria-selected={activeTab === tab.id}
							aria-controls={`writing-context-panel-${tab.id}`}
							onClick={() => setActiveTab(tab.id)}
						>
							{tab.label}
						</button>
					))}
				</div>
			</div>
			<div
				className="monitoring__writing-context-panel-body"
				role="tabpanel"
				id={`writing-context-panel-${activeTab}`}
				aria-labelledby={`writing-context-tab-${activeTab}`}
			>
				<WritingAssistantContextBody run={run} activeTab={activeTab} />
			</div>
		</aside>
	);
}
